import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Home } from "./pages/Home";
import { SignIn, NaverSignIn } from "./pages/SignIn";
import { SignUp } from "./pages/SignUp";
import { Courses } from "./pages/Courses";
import { Course } from "./pages/Courses/[course_id]";
import { Cart } from "./pages/Cart/[user_id]/[course_id]";
import { SuccessPage } from "./pages/Cart/[user_id]/[course_id]/Success";
import { FailPage } from "./pages/Cart/[user_id]/[course_id]/Fail";
import { Policy } from "./pages/Policy";
import { Lecture } from "./pages/Courses/[course_id]/[lecture_id]";
import { LectureInquiry } from "./pages/Courses/[course_id]/[lecture_id]/[course_inquiry_id]";
import { LectureInquiryEdit } from "./pages/Courses/[course_id]/[lecture_id]/[course_inquiry_id]/Edit";
import { NewLectureInquiry } from "./pages/Courses/[course_id]/[lecture_id]/New";
import { QnA } from "./pages/QnA";
import { QnADetail } from "./pages/QnA/[inquiry_id]";
import { QnAEdit } from "./pages/QnA/[inquiry_id]/Edit";
import { NewQnAInquiry } from "./pages/QnA/New";
import {
  LegacyQnAToReviewDetail,
  LegacyQnAToReviewEdit,
  LegacyQnAToReviewList,
  LegacyQnAToReviewNew,
} from "./pages/QnA/reviewBoardRoutes";
import { MyPage } from "./pages/MyPage/Courses";
import { Orders } from "./pages/MyPage/Orders";
import { Order } from "./pages/MyPage/Orders/[payment_id]";
import { Setting } from "./pages/MyPage/Setting";
import { Certificates } from "./pages/MyPage/Certificates";
import { Certificate } from "./pages/MyPage/Certificates/[certificate_id]";
import {
  MyPageInquiries,
  MyPageInquiryWrite,
  MyPageInquiryEdit,
} from "./pages/MyPage/Inquiries";
import { Admin } from "./pages/Admin";
import { Users, StaffCreate } from "./pages/Admin/Users";
import { User } from "./pages/Admin/Users/[user_id]";
import { AdminQnA } from "./pages/Admin/AdminQnA";
import { NewAdminQnAInquiry } from "./pages/Admin/AdminQnA/New";
import { AdminQnADetail } from "./pages/Admin/AdminQnA/[inquiry_id]";
import { Service } from "./pages/Admin/Service";
import { NewService } from "./pages/Admin/Service/New";
import { ServiceEdit } from "./pages/Admin/Service/[course_id]";
import { AdminOrders } from "./pages/Admin/AdminOrders";
import { AdminOrder } from "./pages/Admin/AdminOrders/[payment_id]";
import { AdminCategoryDetail } from "./pages/Admin/Category/[category_name]/[course_inquiry_id]";
import { AdminCounselingDetail } from "./pages/Admin/Counseling/[counseling_id]";
import { FindPassword } from "./pages/FindPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { FAQ } from "./pages/FAQ";
import { FAQDetail } from "./pages/FAQ/[column_id]";
import { FaqCategory } from "./pages/FAQ/Category";
import { NotFound } from "./pages/NotFound";
import { AdminColumn } from "./pages/Admin/Column";
import { NewColumn } from "./pages/Admin/Column/New";
import { AdminColumnEdit } from "./pages/Admin/Column/[column_id]";
import { AdminReviews } from "./pages/Admin/Reviews";
import { NewAdminReview } from "./pages/Admin/Reviews/New";
import { AdminReviewEdit } from "./pages/Admin/Reviews/[review_id]";
import { PageLoadingGate } from "./components/PageLoading";
import { usePageTracking } from "./hooks/usePageTracking";

function PageTracker() {
  usePageTracking();
  return null;
}

// 로그인·회원가입·마이페이지·결제·관리자 등 검색 노출이 불필요한 비공개 경로에
// robots=noindex,nofollow 를 주입한다. (공개 페이지의 robots는 각 페이지의 Seo가 관리)
// 개인정보 보호는 noindex가 아닌 라우트 접근 권한 검증으로 별도 처리한다.
const NOINDEX_PREFIXES = [
  "/signin",
  "/signup",
  "/findpassword",
  "/reset-password",
  "/mypage",
  "/cart",
  "/admin",
];

function RouteRobots() {
  const { pathname } = useLocation();
  useEffect(() => {
    const isPrivate = NOINDEX_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    );
    if (!isPrivate) return;

    let el = document.head.querySelector('meta[name="robots"]');
    let created = false;
    let prev = null;
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
      created = true;
    } else {
      prev = el.getAttribute("content");
    }
    el.setAttribute("content", "noindex, nofollow");

    return () => {
      if (created) {
        el.remove();
      } else if (prev !== null) {
        el.setAttribute("content", prev);
      }
    };
  }, [pathname]);
  return null;
}

function getUserType() {
  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.user?.userType ?? null;
  } catch {
    return null;
  }
}

function AdminProtectedRoute({ allowStaff = false, children }) {
  const userType = getUserType();
  if (userType !== "admin" && !(allowStaff && userType === "staff")) {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

function App() {
  return (
    <Router basename={process.env.PUBLIC_URL}>
      <PageTracker />
      <RouteRobots />
      <PageLoadingGate>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/policy" element={<Policy />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/findpassword" element={<FindPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/signin/naver/callback" element={<NaverSignIn />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/courses/:course_id" element={<Course />} />
        <Route path="/cart/:user_id/:course_id" element={<Cart />} />
        <Route
          path="/cart/:user_id/:course_id/success"
          element={<SuccessPage />}
        />
        <Route path="/cart/:user_id/:course_id/fail" element={<FailPage />} />
        <Route path="/courses/:course_id/:lecture_id" element={<Lecture />} />
        <Route
          path="/courses/:course_id/:lecture_id/:course_inquiry_id"
          element={<LectureInquiry />}
        />
        <Route
          path="/courses/:course_id/:lecture_id/:course_inquiry_id/edit"
          element={<LectureInquiryEdit />}
        />
        <Route
          path="/courses/:course_id/:lecture_id/new"
          element={<NewLectureInquiry />}
        />
        <Route path="/review" element={<QnA />} />
        <Route path="/review/new" element={<NewQnAInquiry />} />
        <Route path="/review/:review_id" element={<QnADetail />} />
        <Route path="/review/:review_id/edit" element={<QnAEdit />} />
        <Route path="/QnA" element={<LegacyQnAToReviewList />} />
        <Route path="/QnA/new" element={<LegacyQnAToReviewNew />} />
        <Route path="/QnA/:inquiry_id/edit" element={<LegacyQnAToReviewEdit />} />
        <Route path="/QnA/:inquiry_id" element={<LegacyQnAToReviewDetail />} />
        <Route path="/mypage/courses" element={<MyPage />} />
        <Route path="/mypage/orders" element={<Orders />} />
        <Route path="/mypage/orders/:payment_id" element={<Order />} />
        <Route path="/mypage/setting" element={<Setting />} />
        <Route path="/mypage/certificates" element={<Certificates />} />
        <Route
          path="/mypage/certificates/:certificate_id"
          element={<Certificate />}
        />
        <Route path="/mypage/inquiries" element={<MyPageInquiries />} />
        <Route path="/mypage/inquiries/new" element={<MyPageInquiryWrite />} />
        <Route
          path="/mypage/inquiries/:inquiry_id/edit"
          element={<MyPageInquiryEdit />}
        />
        <Route path="/admin" element={<Admin />} />

        <Route
          path="/admin/column"
          element={
            <AdminProtectedRoute allowStaff>
              <AdminColumn />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/column/new"
          element={
            <AdminProtectedRoute allowStaff>
              <NewColumn />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/column/:column_id"
          element={
            <AdminProtectedRoute allowStaff>
              <AdminColumnEdit />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/reviews"
          element={
            <AdminProtectedRoute allowStaff>
              <AdminReviews />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/reviews/new"
          element={
            <AdminProtectedRoute allowStaff>
              <NewAdminReview />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/reviews/:review_id"
          element={
            <AdminProtectedRoute allowStaff>
              <AdminReviewEdit />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <AdminProtectedRoute>
              <Users />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/users/staff-create"
          element={
            <AdminProtectedRoute>
              <StaffCreate />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/users/:user_id"
          element={
            <AdminProtectedRoute>
              <User />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/QnA"
          element={
            <AdminProtectedRoute>
              <AdminQnA />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/QnA/:inquiry_id"
          element={
            <AdminProtectedRoute>
              <AdminQnADetail />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/QnA/new"
          element={
            <AdminProtectedRoute>
              <NewAdminQnAInquiry />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/Category/:category_name/:course_inquiry_id"
          element={
            <AdminProtectedRoute>
              <AdminCategoryDetail />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/Counseling/:counseling_id"
          element={
            <AdminProtectedRoute>
              <AdminCounselingDetail />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/service"
          element={
            <AdminProtectedRoute>
              <Service />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/service/new"
          element={
            <AdminProtectedRoute>
              <NewService />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/service/:course_id"
          element={
            <AdminProtectedRoute>
              <ServiceEdit />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/orders"
          element={
            <AdminProtectedRoute>
              <AdminOrders />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/orders/:order_id"
          element={
            <AdminProtectedRoute>
              <AdminOrder />
            </AdminProtectedRoute>
          }
        />
        <Route path="/faq" element={<FAQ />} />
        <Route
          path="/faq/category/:categorySlug"
          element={<FaqCategory />}
        />
        <Route path="/faq/:column_id" element={<FAQDetail />} />
        <Route path="/reviews" element={<LegacyQnAToReviewList />} />
        <Route path="/reviews/:review_id" element={<LegacyQnAToReviewDetail />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </PageLoadingGate>
    </Router>
  );
}

export default App;
